import { ReactNode } from 'react';
import { Flex, Box, Link, LinkProps, useBreakpoint } from '@chakra-ui/react';
import { BreadCrumbs } from '@ifixit/carpenter-breadcrumbs/Breadcrumbs';
import { FlexScrollGradient } from 'Guide/Guide/GuideProducts/FlexScrollGradient';

export interface NavData {
   breadCrumbs?: Array<BreadcrumbData>;
   navTabs?: Array<NavTabData>;
   editMenuComponent?: ReactNode;
}

export interface BreadcrumbData {
   name: string;
   url: string;
}

interface NavTabData {
   isCurrentPage?: boolean;
   name: string;
   url?: string;
}

export function SharedNavBar(props: NavData) {
   const { breadCrumbs, navTabs, editMenuComponent } = props;
   const breakpoint = useBreakpoint({ ssr: false });
   const isMobile = breakpoint == 'base';

   return (
      <Flex flexDirection="column" width="100%">
         <Flex justifyContent="center" borderBottomWidth={1}>
            <Flex
               flexGrow={1}
               height="48px"
               width="100%"
               maxWidth="1280px"
               paddingInline={{ base: 0, sm: 5, lg: 8 }}
               justifyContent="space-between"
            >
               {breadCrumbs && !isMobile && (
                  <Flex flexGrow={1} flexShrink={0} flexBasis={0}>
                     <BreadCrumbs breadCrumbs={breadCrumbs} />
                  </Flex>
               )}
               {navTabs && <NavTabs navTabs={navTabs} />}
               {editMenuComponent}
            </Flex>
         </Flex>
         {breadCrumbs && isMobile && <BreadCrumbs breadCrumbs={breadCrumbs} paddingX={4} />}
      </Flex>
   );
}

function NavTabs({ navTabs }: { navTabs: NavTabData[] }) {
   const baseStyleProps: LinkProps = {
      outline: '2px solid transparent',
      outlineOffset: '2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 'md',
      paddingTop: 2,
      paddingBottom: 2,
      paddingInlineStart: 4,
      paddingInlineEnd: 4,
      position: 'relative',
   };

   const bottomFeedbackStyleProps = {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      borderRadius: '2px 2px 0px 0px',
   };

   const selectedStyleProps = {
      ...baseStyleProps,
      borderColor: 'brand.500',
      color: 'gray.900',
      fontWeight: 500,
      sx: {
         '&:hover': {
            textDecoration: 'none',
            background: 'gray.100',
            '::after': {
               background: 'brand.700',
            },
         },
         '::after': {
            ...bottomFeedbackStyleProps,
            background: 'brand.500',
         },
      },
   };

   const notSelectedStyleProps = {
      ...baseStyleProps,
      borderColor: 'transparent',
      color: 'gray.500',
      fontWeight: 400,
      sx: {
         '&:hover': {
            textDecoration: 'none',
         },
         '&:hover:not(.isDisabled)': {
            textDecoration: 'none',
            color: 'gray.700',
            background: 'gray.100',
         },
         '&.isDisabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            color: 'gray.700',
            background: 'gray.100',
         },
      },
   };

   return (
      <>
         <FlexScrollGradient height="100%" overflow="hidden" flexShrink={1}>
            {navTabs.map((tab: NavTabData) =>
               tab.isCurrentPage ? (
                  tab.url ? (
                     <Link key={tab.name} {...selectedStyleProps} href={tab.url}>
                        {tab.name}
                     </Link>
                  ) : (
                     <Box key={tab.name} {...selectedStyleProps}>
                        {tab.name}
                     </Box>
                  )
               ) : tab.url ? (
                  <Link key={tab.name} {...notSelectedStyleProps} href={tab.url}>
                     {tab.name}
                  </Link>
               ) : (
                  <Box key={tab.name} className="isDisabled" {...notSelectedStyleProps}>
                     {tab.name}
                  </Box>
               )
            )}
         </FlexScrollGradient>
      </>
   );
}
